import React, { useState, useEffect } from 'react';
import { get, post, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function MaterialIssuance({ jobId, jobNumber }) {
  const { user } = useAuth();
  const [usage, setUsage] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ material_id:'', quantity_used:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canIssue = ['super_admin','admin','inventory_officer'].includes(user?.role);

  const load = () => {
    setLoading(true);
    get('/job-costing/job-orders/'+jobId+'/materials')
      .then(d => { setUsage(d.data||[]); setTotalCost(d.total_material_cost||0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    get('/materials').then(d => setMaterials(d.data||[]));
  }, [jobId]);

  const selectedMaterial = materials.find(m => m.id === form.material_id);

  const submit = async () => {
    if (!form.material_id || !form.quantity_used) { setError('Material and quantity required'); return; }
    setSaving(true); setError('');
    try {
      await post('/job-costing/job-orders/'+jobId+'/materials', form);
      setForm({ material_id:'', quantity_used:'', notes:'' });
      setShowForm(false);
      load();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this material usage? Stock will be reversed.')) return;
    try { await del('/job-costing/job-orders/'+jobId+'/materials/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A'}}>
          Material Usage
        </div>
        {canIssue && (
          <button className="btn btn-outline btn-sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Issue Material'}
          </button>
        )}
      </div>

      {showForm && (
        <div style={{background:'#F2EFE7',borderRadius:8,padding:14,marginBottom:14}}>
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Material *</label>
              <select className="form-control" value={form.material_id}
                onChange={e=>setForm({...form,material_id:e.target.value})}>
                <option value="">Select material</option>
                {materials.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} (Stock: {Number(m.current_stock).toLocaleString()} {m.unit_of_measure} — WAC: {fmt(m.weighted_avg_cost)})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Quantity Used *</label>
              <input type="number" className="form-control" value={form.quantity_used}
                onChange={e=>setForm({...form,quantity_used:e.target.value})}
                placeholder="0" />
              {selectedMaterial && form.quantity_used && (
                <div className="form-hint">
                  Cost: {fmt(parseFloat(form.quantity_used||0) * parseFloat(selectedMaterial.weighted_avg_cost||0))}
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" value={form.notes}
              onChange={e=>setForm({...form,notes:e.target.value})}
              placeholder="Any notes about this material usage..." />
          </div>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
            {saving ? 'Issuing...' : 'Issue Material'}
          </button>
        </div>
      )}

      {loading ? <div className="spinner"></div> : usage.length === 0 ? (
        <div style={{padding:'16px',textAlign:'center',background:'#F2EFE7',borderRadius:8,fontSize:12,color:'#8A8A8A'}}>
          No materials issued yet for this job.
        </div>
      ) : (
        <div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:10}}>
            <thead>
              <tr style={{background:'#556B2F'}}>
                {['Material','Unit','Qty Used','Unit Cost','Total Cost','Issued By','Time',''].map(h=>(
                  <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usage.map((u,i) => (
                <tr key={u.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{u.material_name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{u.unit_of_measure}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{Number(u.quantity_used).toLocaleString()}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13}}>{fmt(u.unit_cost)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,fontWeight:600,color:'#556B2F'}}>{fmt(u.total_cost)}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A',fontSize:11}}>{u.issued_by_name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A',fontSize:11}}>{new Date(u.issued_at).toLocaleString('en-NG')}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>
                    {canIssue && (
                      <button className="btn btn-danger btn-sm" style={{fontSize:10,padding:'2px 8px'}}
                        onClick={() => handleDelete(u.id)}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <div style={{background:'#F2EFE7',borderRadius:8,padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',gap:24,minWidth:200}}>
              <span style={{fontSize:12,fontWeight:600,color:'#3B2A21'}}>Total Material Cost</span>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:'#8F2E24'}}>{fmt(totalCost)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function JobCostingSummary({ jobId }) {
  const [costing, setCosting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get('/job-costing/job-orders/'+jobId+'/costing')
      .then(setCosting)
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return <div className="spinner"></div>;
  if (!costing) return null;

  const { summary, material_usage, external_costs } = costing;

  return (
    <div>
      {/* Summary tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14}}>
        <div style={{background:'#E8F5EB',borderRadius:8,padding:'12px 14px',border:'1px solid #B8DEC0'}}>
          <div style={{fontSize:9,color:'#2E6B3A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Revenue</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:'#2E6B3A'}}>{fmt(summary.revenue)}</div>
        </div>
        <div style={{background:'#FDEAEA',borderRadius:8,padding:'12px 14px',border:'1px solid #F5B8B8'}}>
          <div style={{fontSize:9,color:'#8F2E24',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Total Cost</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:'#8F2E24'}}>{fmt(summary.total_cost)}</div>
          <div style={{fontSize:10,color:'#8F2E24',marginTop:2}}>Materials: {fmt(summary.total_material_cost)} + External: {fmt(summary.total_external_cost)}</div>
        </div>
        <div style={{background:summary.gross_profit>=0?'#E8F5EB':'#FDEAEA',borderRadius:8,padding:'12px 14px',border:'1px solid '+(summary.gross_profit>=0?'#B8DEC0':'#F5B8B8')}}>
          <div style={{fontSize:9,color:summary.gross_profit>=0?'#2E6B3A':'#8F2E24',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Gross Profit</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:summary.gross_profit>=0?'#2E6B3A':'#8F2E24'}}>{fmt(summary.gross_profit)}</div>
          <div style={{fontSize:10,color:summary.gross_profit>=0?'#2E6B3A':'#8F2E24',marginTop:2}}>Margin: {summary.margin}%</div>
        </div>
      </div>

      {/* External costs (DTF bills) */}
      {external_costs.length > 0 && (
        <div style={{marginTop:12}}>
          <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:8}}>External Costs (DTF / Supplier Bills)</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#F2EFE7'}}>
              {['Bill No','Supplier','Description','Allocated Amount'].map(h=>(
                <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {external_costs.map((c,i) => (
                <tr key={i}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{c.bill_number}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{c.supplier_name}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{c.description}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,fontWeight:600,color:'#8F2E24'}}>{fmt(c.allocated_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {external_costs.length === 0 && summary.revenue > 0 && (
        <div style={{marginTop:10,padding:'10px 14px',background:'#FFF3DC',borderRadius:8,fontSize:12,color:'#8B5E0A'}}>
          No external costs (DTF bills) linked to this job yet. Link bills from the Bills & Expenses page.
        </div>
      )}
    </div>
  );
}

export function BillJobAllocation({ billId, billAmount, onSaved }) {
  const [jobOrders, setJobOrders] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      get('/job-orders'),
      get('/job-costing/bills/'+billId+'/job-allocations')
    ]).then(([jobs, allocs]) => {
      setJobOrders(jobs.data||[]);
      setExistingAllocations(allocs.data||[]);
      setSelectedJobs((allocs.data||[]).map(a=>a.job_order_id));
    }).finally(() => setLoading(false));
  }, [billId]);

  const toggleJob = (id) => {
    setSelectedJobs(prev => prev.includes(id) ? prev.filter(j=>j!==id) : [...prev, id]);
  };

  const selectedJobDetails = jobOrders.filter(j=>selectedJobs.includes(j.id));
  const totalQty = selectedJobDetails.reduce((sum,j)=>sum+parseFloat(j.quantity||0),0);

  const preview = selectedJobDetails.map(j => ({
    ...j,
    allocated: totalQty > 0 ? ((parseFloat(j.quantity||0)/totalQty)*parseFloat(billAmount)).toFixed(2) : 0,
    percentage: totalQty > 0 ? ((parseFloat(j.quantity||0)/totalQty)*100).toFixed(1) : 0
  }));

  const submit = async () => {
    if (!selectedJobs.length) { setError('Select at least one job order'); return; }
    setSaving(true); setError('');
    try {
      await post('/job-costing/bills/'+billId+'/job-allocations', { job_order_ids: selectedJobs });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      {error && <div className="alert alert-error mb-md">{error}</div>}

      <div style={{background:'#F2EFE7',borderRadius:8,padding:12,marginBottom:14,fontSize:13}}>
        <div style={{display:'flex',justifyContent:'space-between'}}>
          <span style={{color:'#8A8A8A'}}>Bill Amount to Allocate</span>
          <strong style={{fontFamily:'Cormorant Garamond,serif',fontSize:15,color:'#8F2E24'}}>{fmt(billAmount)}</strong>
        </div>
      </div>

      <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>
        Select Job Orders (auto-split by quantity)
      </div>

      <div style={{maxHeight:300,overflowY:'auto',marginBottom:14}}>
        {jobOrders.filter(j=>j.status!=='cancelled').map(j => (
          <div key={j.id}
            style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',
              background:selectedJobs.includes(j.id)?'#F2EFE7':'#fff',
              border:'1px solid '+(selectedJobs.includes(j.id)?'#556B2F':'var(--border-light)'),
              borderRadius:8,marginBottom:6,cursor:'pointer'}}
            onClick={() => toggleJob(j.id)}>
            <input type="checkbox" checked={selectedJobs.includes(j.id)} onChange={()=>toggleJob(j.id)} onClick={e=>e.stopPropagation()} />
            <div style={{flex:1}}>
              <div style={{fontWeight:500,fontSize:13}}>{j.job_number} — {j.customer_name}</div>
              <div style={{fontSize:11,color:'#8A8A8A'}}>{j.product_description} | Qty: {j.quantity}</div>
            </div>
          </div>
        ))}
      </div>

      {preview.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:8}}>Cost Allocation Preview</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#556B2F'}}>
              {['Job Order','Product','Qty','%','Allocated Amount'].map(h=>(
                <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {preview.map((j,i) => (
                <tr key={j.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{j.job_number}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{j.product_description||'—'}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{j.quantity}</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8'}}>{j.percentage}%</td>
                  <td style={{padding:'7px 10px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:13,fontWeight:600,color:'#8F2E24'}}>{fmt(j.allocated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button className="btn btn-primary" onClick={submit} disabled={saving||!selectedJobs.length}>
        {saving ? 'Saving...' : 'Save Allocations'}
      </button>
    </div>
  );
}
